import { useEffect, useState } from 'react';
import { createIntl, useIntl, type IntlShape } from 'react-intl';
import { loadTranslations, resolveTranslationLanguage } from 'translations';

const getBaseLanguage = (locale: string) => locale.split('-')[0].toLowerCase();

// Names written into a template, such as a new section or field, are its
// content: they follow the template's language, not the interface language.
// Until the template language is loaded, the interface language names them.
export const useTemplateIntl = (language: string | undefined): IntlShape => {
  const intl = useIntl();
  const [templateIntl, setTemplateIntl] = useState<IntlShape | null>(null);
  const foreign =
    !!language && getBaseLanguage(language) !== getBaseLanguage(intl.locale);

  useEffect(() => {
    if (!foreign || !language || !resolveTranslationLanguage(language)) {
      return undefined;
    }
    const controller = new AbortController();
    loadTranslations(language)
      .then((messages) => {
        if (!controller.signal.aborted) {
          setTemplateIntl(
            createIntl({
              locale: language,
              messages,
              onError: () => undefined,
            }),
          );
        }
      })
      .catch(() => {
        /* ignore — new names then stay in the interface language */
      });
    return () => controller.abort();
  }, [foreign, language]);

  return foreign && templateIntl?.locale === language ? templateIntl : intl;
};
